import { Ref } from 'vue';
import { keepPreviousData, useQuery } from '@tanstack/vue-query';
import { AxiosError, AxiosResponse } from 'axios';

import { useDeviceStore } from '@/stores/device';

import { api } from './_http';

export type GetMasterListData = {
	master_list_onsub: masterListSubscription[];
	master_list_nosub: masterListSubscription[];
};
export type masterListSubscription = {
	master_pk: number;
	master_exposure_seq: number;
	master_name: string;
	file_pk: number;
	file_path: string;
};
export const useGetMasterListApi = (enabled?: Ref<boolean>) => {
	const url = '/v1/front/getMasterList';
	return useQuery<AxiosResponse, AxiosError, GetMasterListData>({
		queryKey: [url],
		queryFn: () => api.get(url),
		select: (res) => res.data?.data,
		placeholderData: keepPreviousData,
		enabled: enabled ?? true,
	});
};

type GetMasterDetailParams = {
	master_pk: number | null;
};
type GetMasterDetailData = {
	master: GetMasterDetailDefaultData;
	master_sector: GetMasterDetailSectorData[];
};
export type GetMasterDetailDefaultData = {
	master_pk: number;
	master_name: string;
	master_motto: string;
	master_feat: string;
	master_rate?: string;
	master_investment_rule: string;
	master_investment_style: string;
	master_introduce_1?: string;
	master_introduce_2?: string;
	file_pk?: number;
	file_path: string;
	master_rebalance_date?: string;
	master_trading_style?: string;
	master_trading_description: string;
};

export type GetMasterDetailSectorData = {
	master_sector_pk: number;
	master_sector_type: string;
	master_sector_ratio: number;
	master_pk: number;
};
export const useGetMasterDetailApi = (params: Ref<GetMasterDetailParams>) => {
	const url = '/v1/front/getMasterDetail';

	return useQuery<AxiosResponse, AxiosError, GetMasterDetailData>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => res.data?.data,
		placeholderData: keepPreviousData,
		enabled: false,
	});
};

type GetMasterEventParams = {
	master_pk: number | null;
	sub_yn: number | null; // blur 처리 지금은 사용 안하는 중
	master_investment_category: string;
};
type GetMasterEventData = {
	master_investment: GetMasterEventInvestmentData[];
};
export type GetMasterEventInvestmentData = {
	bookmark: number;
	event_name: string;
	event_pk: number;
	master_investment_pk: number;
	score: number;
	file_path?: string;
	event_ticker?: string;
	event_code?: string;
};
export const useGetMasterEventApi = (params: Ref<GetMasterEventParams>) => {
	const deviceStore = useDeviceStore();
	const url = '/v1/front/getMasterEvent';
	return useQuery<AxiosResponse, AxiosError, GetMasterEventData>({
		queryKey: [url, params],
		queryFn: () => api.get(url, { ...params.value, device: deviceStore.getDevice }),
		select: (res) => res.data?.data,
		placeholderData: keepPreviousData,
		enabled: false,
	});
};
//-------------------------------------------------------------------
//이달의 포착주 조회(미국주식)
type GetMasterDetailUSAParams = {
	master_pk: string;
};
export type Master = {
	master_pk: number;
	master_name: string;
	master_motto: string;
	master_feat: string;
	master_investment_rule: string;
	master_investment_style: string;
	file_path: string;
	master_trading_description: string;
	master_trading_style: string;
};

export type MonthlyCapture = {
	bookmark?: number | null;
	event_name: string;
	event_pk: number;
	event_ticker?: string;
	event_code?: string;
	event_color?: string;
	master_name?: string;
	master_pk?: number;
	file_path?: string;
	file_pk?: number;
	outline_enrollment_date?: string;
	outline_price?: string;
	etf_pk?: number;
	etf_event_pk?: number;
};
export type MasterSector = {
	'원자재/소재': number; //원자재/소재
	정보기술: number; //정보기술
	헬스케어: number; //헬스케어
	금융: number; //금융
	필수소비재: number; //필수소비재
	임의소비재: number; //임의소비재
	부동산: number; // 부동산
	산업: number; //산업
	유틸리티: number; //유틸리티
	통신서비스: number; //통신서비스
	에너지: number; //에너지
};

type GetMasterDetailUSAData = {
	master: Master;
	base_date: string;
	monthly_capture: MonthlyCapture[];
	master_sector: {
		count: MasterSector;
		ratio: MasterSector;
	};
};

export const useGetMasterDetailApi_USA = (params: Ref<GetMasterDetailUSAParams>) => {
	const url = '/v1/front/usa/getMasterDetail';
	return useQuery<AxiosResponse, AxiosError, GetMasterDetailUSAData>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => res.data?.data,
		enabled: false,
		placeholderData: keepPreviousData,
	});
};

//-------------------------------------------------------------------
//이달의 포착주 조회(일본주식)
type GetMasterDetailJPNParams = {
	master_pk: string;
};

type GetMasterDetailJPNData = {
	master: Master;
	base_date: string;
	monthly_capture: MonthlyCapture[];
	master_sector: {
		count: MasterSector;
		ratio: MasterSector;
	};
};

export const useGetMasterDetailApi_JPN = (params: Ref<GetMasterDetailJPNParams>) => {
	const url = '/v1/front/jpn/getMasterDetail';
	return useQuery<AxiosResponse, AxiosError, GetMasterDetailJPNData>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => res.data?.data,
		enabled: false,
		placeholderData: keepPreviousData,
	});
};
